import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  AddUploadDocumentViewerComponent,
  AddUploadResult,
} from '@lib/components/pdf-viewer/add-upload/add-upload-document-viewer.component';
import { FichierOperation, Operation, OperationStateTransitionTrigger } from '@lib/models/generated/graphql';
import { LocalFileInfo } from '@lib/models/LocalFileInfo';
import { DocumentsService } from '@lib/services/documents.service';
import { OperationsService } from '@lib/services/operations.service';
import { openFilePicker } from '@lib/utils/filesUtils';
import { OuiDialogService } from 'omnium-ui/dialog';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { DeclareMissingFileComponent } from 'src/app/components/control-file-pdf-viewer/declare-missing-file/declare-missing-file.component';
import { TransmissionPartenaireDialogComponent } from 'src/app/components/control-file-pdf-viewer/transmission-partenaire-dialog/transmission-partenaire-dialog.component';
import { FichierOperationService } from 'src/services/fichier-operation.service';
import { InvestisseurService } from 'src/services/investisseur.service';
@Component({
    selector: 'app-no-fichier-operation',
    templateUrl: './no-fichier-operation.component.html',
    styleUrls: ['./no-fichier-operation.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NoFichierOperationComponent {
  @Input({ required: true }) operation: Operation;
  @Input({ required: true }) isInstanceMode: boolean;
  @Output() operationChange = new EventEmitter<void>();
  isFileUploading = false;

  constructor(
    private operationService: OperationsService,
    private fichierOperationService: FichierOperationService,
    private documentService: DocumentsService,
    private investisseurService: InvestisseurService,
    private ouiDialog: OuiDialogService,
    private snackbarService: OuiSnackbarService,
    private dialogModal: MatDialog
  ) {}

  onMissingFile() {
    const modalRef = this.ouiDialog.openDialog(
      DeclareMissingFileComponent,
      {
        operationId: this.operation.id,
        isInstanceMode: this.isInstanceMode,
      },
      'auto',
      '540px'
    );

    modalRef.afterClosed().subscribe((missingFile: FichierOperation) => {
      if (missingFile && missingFile.id) {
        //update
        this.operationChange.emit();
      }
    });
  }

  onAddFichierOperation() {
    openFilePicker(
      'target_div',
      (files: File[]) => {
        if (!files.every(file => this.documentService.isAuthorizedConsultantFile(file))) {
          this.snackbarService.open('Format ou poids de fichier non autorisé.', 'error', 5000, {
            horizontal: 'left',
            vertical: 'bottom',
          });
          return;
        }
        const dialogRef = this.dialogModal.open(AddUploadDocumentViewerComponent, {
          height: '100%',
          width: '100%',
          panelClass: 'full-screen-modal',
          autoFocus: false,
          data: {
            files,
            typeSelectable: true,
            typeLabel: null,
            investisseurs: this.investisseurService.getAllInvestisseurs(),
          },
        });
        dialogRef.afterClosed().subscribe(async (result: AddUploadResult) => {
          if (Array.isArray(result?.filesInfo) && result?.filesInfo?.length > 0) {
            this.isFileUploading = true;

            // first we upload a file  to the server then we return fileId to create fichier Operation.
            const localFileInfos: LocalFileInfo[] = result.filesInfo;
            const lfi: LocalFileInfo = localFileInfos[0];
            const investisseurId = localFileInfos[0].investisseurId;
            if (!investisseurId) return;
            const fileId = await this.documentService.uploadFile(
              lfi.file,
              investisseurId,
              lfi.boxDocumentType,
              lfi.denomination
            );
            // then we create FichierOperation with the fileId
            if (fileId) {
              this.investisseurService.updateInvestisseurFileData(investisseurId);
              await this.fichierOperationService.createFichierOperation(
                fileId,
                this.operation.id,
                !!investisseurId && investisseurId === this.investisseurService.getCoInvestisseur()?.id,
                lfi.comment
              );
              this.isFileUploading = false;
              this.operationChange.emit();
            }
          }
        });
      },
      false
    );
  }

  // transmettre au partenaire
  onSendToPartenaire(): void {
    const modalRef = this.ouiDialog.openDialog(TransmissionPartenaireDialogComponent, {
      operationId: this.operation.id,
    });

    modalRef.afterClosed().subscribe(async (trigger?: OperationStateTransitionTrigger) => {
      if (trigger) {
        const sentResult = await this.operationService.fireOperationStateTransitionTrigger(
          this.operation.activeOperationStateTransitionTriggers,
          this.operation.id,
          trigger,
          null
        );
        if (sentResult) {
          this.operationChange.emit();
        }
      }
    });
  }

  canSendToPartenaire(): boolean {
    return this.operationService.canSendToPartenaire(this.operation.activeOperationStateTransitionTriggers);
  }
}
